<template>
<div>
  <highcharts class="chart" :options="options" ref="highchart" />
  <el-scrollbar class="status">
    <ul class="list list-event">
      <li v-for="point in legendPoints" :key="point.key" :class="getLegendClass(point.series)" :style="{borderColor: point.color}" >
        <span class="number">{{ convertValuByUnit(point) }}</span>
        <span class="content" @click="onClickLegend(point.series)">
          <strong v-if="showObjName">{{ convertObjName(point.series) }}</strong>
          <span>{{ convertLegendName(point.series) }}</span>
        </span>
      </li>
    </ul>
  </el-scrollbar>
  <w-transaction-detail-popup ref="detailPopup" popup-path="analysis" :scatter-data="scatterChartData" :tx-type="txTypeLabel" />
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { cloneDeep, last, findLast, isEmpty as objectIsEmpty } from 'lodash';

import { isEmpty } from '@/utils/basic';
import chartUtil from '@/utils/charts';
import { lineOptions } from '@/constants/chart-options';
import ChartAPI from '@/api/performance/analysis/analysis-chart';
import WTransactionDetailPopup from '@/views/apm/transaction-matrix/components/popup/TransactionDetailPopup';

export default {
  name: 'WCommonLineChart',
  components: { WTransactionDetailPopup },
  props: {
    active: Boolean,
    visible: Boolean,
    rscInfoList: Array,
    showObjName: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      options: cloneDeep(lineOptions),
      legendPoints: [],
      scatterChartData: {},
      txTypeLabel: '소요시간(ms)', // service에서 dataType의 default값은 "elapsed"이므로 label은 "소요시간(ms)"로 표현.
    };
  },
  computed: mapGetters(['analysisDateParams', 'selectedNode', 'isRelativeAnalysisDate']),
  watch: {
    active() {
      this.onShowChart();
    },
    selectedNode: {
      immediate: true,
      handler() {
        this.onShowChart();
      },
    },
    /**
     * subchart의 visible 여부에 따라 rscInfo의 데이터를 받아와 차트 series를 바인딩해준다.
     * @return {[type]} [description]
     * @author jda
     */
    visible() {
      this.onShowChart();
    },
    /**
     * 기간필터에서 기간 변경시 차트를 reload한다.
     * @return {Boolean}       [description]
     * @author jda
     */
    analysisDateParams() {
      this.onShowChart();
    },
  },
  mounted() {
    const me = this;
    const obj = this.selectedNode.obj;
    const params = {
      objId: obj.objId,
      requestType: obj.objDefineId === 'WAS' ? 'instance' : null,
    };
    Object.assign(this.options.chart, {
      zoomType: 'x',
      height: 97,
      events: {
        selection(e) {
          const minX = e.xAxis[0].min.toFixed(0);
          const maxX = e.xAxis[0].max.toFixed(0);

          let scatterData = {
            startDatetime: minX,
            endDatetime: maxX,
            from: 0, // 최소값
            to: 9999, // 최대값
          };
          scatterData = Object.assign(params, scatterData);
          // 19.12.20 관리 구분 상관 없이 분석 기간 store 상태 값 관리되도록 추가
          me.$store.dispatch('setSelectedAnalysisChartDate', {
            startDatetime: Number.parseFloat(minX),
            endDatetime: Number.parseFloat(maxX),
            scatterData,
          });
          // me.scatterChartData = scatterData;
          // me.$refs.detailPopup.open();
          return false;
        },
      },
    });

    Object.assign(this.options.yAxis, {
      tickPositioner() {
        const tickPositions = this.tickPositions;
        if (this.dataMax !== null && this.dataMin !== null) {
          const positions = [];
          if (this.dataMax - this.dataMin < 1) {
            tickPositions.forEach((position) => {
              const p = Math.ceil(position);
              if (positions.indexOf(p) < 0) {
                positions.push(p);
              }
            });
            console.log(this.series[0].name, positions, this.tickPositions);
            return positions;
          }
          return tickPositions;
        }
        return tickPositions;
      },
    });

    Object.assign(this.options.plotOptions.series, {
      point: {
        events: {
          mouseOver() {
            me.setCustomTooltip();
          },
        },
      },
      events: {
        mouseOut() {
          me.setCustomTooltip('init');
        },
      },
    });
  },
  methods: {
    /**
     * 범례에 표현할 이름을 반환한다.
     * @param  {[type]} series [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertLegendName(series) {
      return chartUtil.convertLegendName(series);
    },
    /**
     * 범례의 장비명을 표현한다.
     * @param  {[type]} series [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertObjName(series) {
      return chartUtil.convertLegendObjName(series);
    },
    /**
     * 범례 보이기/숨김 처리를 한다.
     * 최대 8개의 항목만 활성화 할 수 있다.
     * @param  {[type]} series [description]
     * @return {[type]}        [description]
     * @author jda
     */
    onClickLegend(currentSeries) {
      chartUtil.clickLegend(this, currentSeries);
    },
    /**
     * 범례 활성화/비활성화의 className을 반환한다.
     * @param  {[type]} currentSeries [description]
     * @return {[type]}               [description]
     * @author jda
     */
    getLegendClass(currentSeries) {
      return chartUtil.getLegendClass(currentSeries);
    },
    /**
     * 차트의 데이터를 단위변환 한다.
     * @param  {[type]} point [description]
     * @return {[type]}       [description]
     * @author jda
     */
    convertValuByUnit(point) {
      return chartUtil.convertChartValuByUnit(point);
    },
    /**
     * 차트 옆에 툴팁 겸 범례를 표현한다.
     * 1. type이 init일 경우 가장 최근의 데이터를 표현한다.
     * 2. 그 외의 데이터는 마우스 오버 했을 시 해당 포인트르르 표현한다.
     * @param  {[type]} type [description]
     * @author jda
     */
    setCustomTooltip(type) {
      if (isEmpty(this.$refs.highchart)) {
        return;
      }
      const chart = this.$refs.highchart.chart;
      const points = [];
      if (!isEmpty(type) && type === 'init') {
        chart.series.forEach((record) => {
          const point = last(record.points);
          if (point) {
            points.push(point);
          }
        });
      } else {
        chart.series.forEach((record) => {
          const point = record.searchPoint(event);
          if (point) {
            points.push(point);
          } else {
            const finalPoint = findLast(record.points);
            if (!isEmpty(finalPoint)) {
              points.push(finalPoint);
            }
          }
        });
      }
      this.legendPoints = points;
    },
    /**
     * 각 자원별 차트 데이터를 그려준다.
     * @return {[type]} [description]
     * @author jda
     */
    onShowChart() {
      if (this.visible && this.active) {
        const selectedNode = this.selectedNode;
        if (objectIsEmpty(selectedNode)) {
          return;
        }

        const params = Object.assign({}, this.analysisDateParams, {
          managetypeId: selectedNode.obj.objManagetypeId,
          isRelative: this.isRelativeAnalysisDate,
          objId: selectedNode.obj.objId,
          rscInfoList: this.rscInfoList,
        });

        ChartAPI.getPerformanceChartData(params).then((response) => {
          if (response.data.success) {
            this.options.series = response.data.data;

            this.$nextTick(() => {
              this.setCustomTooltip('init');
            });
          }
        });
      } else {
        this.legendPoints = [];
      }
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/performance/node/analysis/apm/components/chart/CommonLineChart.vue